import { reactive } from 'vue';
import isDefinedAndNotEmpty from '../isDefinedAndNotEmpty.js';
import smartLogger from './shared/tools/smartLogger.js';

const log = smartLogger;

function getRuntimeEnvValue(key) {
  const k = typeof key === 'string' ? key.trim() : '';
  if (k.length === 0) {
    return null;
  }

  const isRealValue = (val) => {
    if (typeof val !== 'string' || val.trim().length === 0) {
      return false;
    }
    const s = val.trim();
    if (s.indexOf('${') >= 0) {
      return false;
    }
    if (s.startsWith('__') && s.endsWith('__')) {
      return false;
    }
    return true;
  };

  if (typeof window !== 'undefined' && window.env !== undefined && window.env !== null) {
    const v = window.env[k];
    if (isRealValue(v) === true) {
      return v.trim();
    }
  }

  let processValue = undefined;
  try {
    if (k === 'VUE_APP_API_URL') { processValue = process.env.VUE_APP_API_URL; }
    else if (k === 'VUE_APP_WHITELABEL_NAME') { processValue = process.env.VUE_APP_WHITELABEL_NAME; }
    else if (k === 'VUE_APP_WHITELABEL_LOGO') { processValue = process.env.VUE_APP_WHITELABEL_LOGO; }
    else if (k === 'VUE_APP_UPLOAD_VERSION_MODE') { processValue = process.env.VUE_APP_UPLOAD_VERSION_MODE; }
    else if (k === 'environment') { processValue = process.env.environment; }
    else {
      const processEnv = typeof process !== 'undefined' && process.env !== undefined && process.env !== null ? process.env : null;
      processValue = processEnv !== null ? processEnv[k] : undefined;
    }
  } catch (e) {}

  if (isRealValue(processValue) === true) {
    return processValue.trim();
  }

  return null;
}

function isTruthyRuntimeValue(value) {
  if (typeof value !== 'string') {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === 'true') {
    return true;
  }
  if (normalized === '1') {
    return true;
  }
  if (normalized === 'yes') {
    return true;
  }
  if (normalized === 'on') {
    return true;
  }
  return false;
}

export function isHttpOnlyAuthEnabled() {
  const keys = ['httpOnly', 'HTTP_ONLY', 'VUE_APP_HTTP_ONLY', 'VUE_APP_HTTP_ONLY_AUTH', 'VUE_APP_AUTH_HTTP_ONLY', 'authHttpOnly', 'useHttpOnlyCookies', 'useHttpCookie', 'VUE_APP_USE_HTTP_COOKIE'];
  for (let i = 0; i < keys.length; i = i + 1) {
    if (isTruthyRuntimeValue(getRuntimeEnvValue(keys[i])) === true) {
      return true;
    }
  }
  return false;
}

function normalizeBaseUrl(value) {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (raw.length === 0) {
    return '';
  }
  const withoutTrailingSlash = raw.endsWith('/') === true ? raw.slice(0, raw.length - 1) : raw;
  return withoutTrailingSlash;
}

function getCookie(name) {
  if (typeof document === 'undefined' || typeof document.cookie !== 'string') {
    return null;
  }
  const cookies = document.cookie.split('; ');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.split('=');
    if (decodeURIComponent(cookieName) === name) {
      try {
        return decodeURIComponent(cookieValue);
      } catch (e) {
        log.error('getCookie', 'Error decoding cookie value:', e);
        return null;
      }
    }
  }
  return null;
}

function loadUserInfoFromCookie() {
  const userDataCookie = getCookie('userData');
  if (userDataCookie !== null) {
    try {
      const userInfo = JSON.parse(userDataCookie);
      return userInfo;
    } catch (e) {
      log.error('loadUserInfoFromCookie', 'Error parsing userData cookie:', e);
      const runtimeEnv = getRuntimeEnvValue('environment');
      const sameSiteLax = (runtimeEnv === 'production') ? '; SameSite=Lax' : '';
      document.cookie = `userData=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${sameSiteLax}`; 
      return null;
    }
  }
  return null;
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
}

function readCookieRawValue(name) {
  if (isHttpOnlyAuthEnabled() === true) {
    if (name === 'authToken') {
      return '';
    }
    if (name === 'jwtToken') {
      return '';
    }
  }
  const v = getCookie(name);
  if (typeof v === 'string' && v.trim().length > 0 && v !== 'undefined') {
    return v;
  }
  return '';
}

function parseJwtPayload(token) {
  if (typeof token !== 'string' || token.trim().length === 0) {
    return null;
  }
  const parts = token.split('.');
  if (Array.isArray(parts) === false || parts.length < 2) {
    return null;
  }
  try {
    const payloadJson = atob(parts[1]);
    const parsed = safeJsonParse(payloadJson);
    if (parsed !== null && parsed !== undefined) {
      return parsed;
    }
    return null;
  } catch (e) {
    return null;
  }
}

function normalizeStringArray(value) {
  if (Array.isArray(value) === false) {
    return [];
  }
  const out = [];
  for (const v of value) {
    if (typeof v === 'string' && v.trim().length > 0) {
      out.push(v.trim());
    }
  }
  return [...new Set(out)];
}

function buildUserInfoFromCookies() {
  const userDataRaw = readCookieRawValue('userData');
  const userData = userDataRaw.length > 0 ? safeJsonParse(userDataRaw) : null;
  const authToken = readCookieRawValue('authToken');
  const jwtToken = readCookieRawValue('jwtToken');
  const token = authToken.length > 0 ? authToken : jwtToken;
  const payload = token.length > 0 ? parseJwtPayload(token) : null;

  const permissionsFromUserData = userData !== null && userData !== undefined ? (userData.permissions !== undefined ? userData.permissions : userData.Permissions) : undefined;
  const rolesFromUserData = userData !== null && userData !== undefined ? (userData.roles !== undefined ? userData.roles : userData.Roles) : undefined;
  const permissionsFromPayload = payload !== null && payload !== undefined ? payload.permissions : undefined;
  const rolesFromPayload = payload !== null && payload !== undefined ? payload.roles : undefined;

  const permissions = normalizeStringArray(Array.isArray(permissionsFromUserData) === true ? permissionsFromUserData : permissionsFromPayload);
  const roles = normalizeStringArray(Array.isArray(rolesFromUserData) === true ? rolesFromUserData : rolesFromPayload);

  const userId = payload !== null && payload !== undefined ? (payload.userID !== undefined ? payload.userID : payload.userId) : undefined;
  const organizationID = payload !== null && payload !== undefined ? (payload.organizationID !== undefined ? payload.organizationID : payload.organizationId) : undefined;
  const userName = payload !== null && payload !== undefined ? (payload.userName !== undefined ? payload.userName : payload.username) : undefined;
  const organizationName = payload !== null && payload !== undefined ? payload.organizationName : undefined;
  const subscription = payload !== null && payload !== undefined && typeof payload.subscription === 'string' ? payload.subscription : undefined;
  const allowedSourcesFromUserData = userData !== null && userData !== undefined ? userData.allowedSources : undefined;
  const allowedSourcesFromPayload = payload !== null && payload !== undefined ? payload.allowedSources : undefined;
  const allowedSources = normalizeStringArray(Array.isArray(allowedSourcesFromUserData) === true ? allowedSourcesFromUserData : allowedSourcesFromPayload);

  if (permissions.length === 0 && roles.length === 0 && token.length === 0 && userData === null) {
    return null;
  }

  return {
    userId: userData !== null && userData !== undefined ? (userData.userId !== undefined ? userData.userId : userData.userID) : userId,
    userName: userData !== null && userData !== undefined ? userData.userName : userName,
    organizationID: userData !== null && userData !== undefined ? userData.organizationID : organizationID,
    organizationName: userData !== null && userData !== undefined ? userData.organizationName : organizationName,
    subscription: userData !== null && userData !== undefined && userData.subscription !== undefined && userData.subscription !== null && String(userData.subscription).trim() !== '' ? userData.subscription : subscription,
    allowedSources: allowedSources,
    permissions: permissions,
    roles: roles
  };
}

export function refreshUserInfoFromCookies() {
  const resolved = buildUserInfoFromCookies();
  if (resolved === null) {
    globalState.userInfo = null;
    globalState.userInfoValidatedAt = null;
    return null;
  }
  if (globalState.userInfo === null || globalState.userInfo === undefined) {
    globalState.userInfo = resolved;
    return resolved;
  }
  const currentPermissions = Array.isArray(globalState.userInfo.permissions) === true ? globalState.userInfo.permissions : [];
  const currentRoles = Array.isArray(globalState.userInfo.roles) === true ? globalState.userInfo.roles : [];
  const nextPermissions = Array.isArray(resolved.permissions) === true ? resolved.permissions : [];
  const nextRoles = Array.isArray(resolved.roles) === true ? resolved.roles : [];
  if (currentPermissions.length === 0 && nextPermissions.length > 0) {
    globalState.userInfo.permissions = nextPermissions;
  }
  if (currentRoles.length === 0 && nextRoles.length > 0) {
    globalState.userInfo.roles = nextRoles;
  }
  if ((globalState.userInfo.userId === undefined || globalState.userInfo.userId === null) && (resolved.userId !== undefined && resolved.userId !== null)) {
    globalState.userInfo.userId = resolved.userId;
  }
  if ((globalState.userInfo.userName === undefined || globalState.userInfo.userName === null) && (resolved.userName !== undefined && resolved.userName !== null)) {
    globalState.userInfo.userName = resolved.userName;
  }
  if ((globalState.userInfo.organizationID === undefined || globalState.userInfo.organizationID === null) && (resolved.organizationID !== undefined && resolved.organizationID !== null)) {
    globalState.userInfo.organizationID = resolved.organizationID;
  }
  if ((globalState.userInfo.organizationName === undefined || globalState.userInfo.organizationName === null) && (resolved.organizationName !== undefined && resolved.organizationName !== null)) {
    globalState.userInfo.organizationName = resolved.organizationName;
  }
  if ((globalState.userInfo.subscription === undefined || globalState.userInfo.subscription === null || globalState.userInfo.subscription === '') && (resolved.subscription !== undefined && resolved.subscription !== null && resolved.subscription !== '')) {
    globalState.userInfo.subscription = resolved.subscription;
  }
  if (Array.isArray(globalState.userInfo.allowedSources) !== true && Array.isArray(resolved.allowedSources) === true) {
    globalState.userInfo.allowedSources = resolved.allowedSources;
  }
  return globalState.userInfo;
}

export function getMergedRolesAndPermissions() {
  const resolved = buildUserInfoFromCookies();
  const g = globalState.userInfo;
  const gPerms = g !== null && g !== undefined && Array.isArray(g.permissions) === true ? normalizeStringArray(g.permissions) : [];
  const gRoles = g !== null && g !== undefined && Array.isArray(g.roles) === true ? normalizeStringArray(g.roles) : [];
  const cPerms = resolved !== null && resolved !== undefined && Array.isArray(resolved.permissions) === true ? normalizeStringArray(resolved.permissions) : [];
  const cRoles = resolved !== null && resolved !== undefined && Array.isArray(resolved.roles) === true ? normalizeStringArray(resolved.roles) : [];
  const roles = [...new Set([...gRoles, ...cRoles])];
  const permissions = [...new Set([...gPerms, ...cPerms])];
  return { roles, permissions };
}

function loadWhiteLabelSettings() {
  log.debug('loadWhiteLabelSettings', 'loadWhiteLabelSettings called');
  const envLogo = getRuntimeEnvValue('VUE_APP_WHITELABEL_LOGO') !== null ? getRuntimeEnvValue('VUE_APP_WHITELABEL_LOGO') : getRuntimeEnvValue('VUE_APP_WHITE_LABEL_LOGO_URL');
  let resolvedLogo = null;
  if (isDefinedAndNotEmpty(envLogo) != false) {
    resolvedLogo = String(envLogo).trim();
  }
  const envName = getRuntimeEnvValue('VUE_APP_WHITELABEL_NAME') !== null ? getRuntimeEnvValue('VUE_APP_WHITELABEL_NAME') : getRuntimeEnvValue('VUE_APP_WHITE_LABEL_COMPANY_NAME');
  return {
    enabled: isDefinedAndNotEmpty(envLogo) != false || isDefinedAndNotEmpty(envName) != false,
    logoUrl: resolvedLogo,
    organizationLogoUrl: null,
    organizationEnabled: false,
    vendorLogoUrl: null,
    appName: isDefinedAndNotEmpty(envName) != false ? envName : 'GenChatAI',
  };
}

function saveSettingsToCookie(settings) {
    const cookieValue = JSON.stringify({
        timezone: settings.timezone,
        language: settings.language,
        allowedSources: normalizeStringArray(settings.allowedSources)
    });
    const expires = new Date();
    expires.setDate(expires.getDate() + 30); 
    const runtimeEnv = getRuntimeEnvValue('environment');
    const sameSiteLax = (runtimeEnv === 'production') ? '; SameSite=Lax' : '';
    document.cookie = `orgSettings=${encodeURIComponent(cookieValue)}; path=/; expires=${expires.toUTCString()}${sameSiteLax}`;
    log.debug('saveSettingsToCookie', `Saved org settings to cookie: ${cookieValue}`);
}

function loadSettingsFromCookie() {
    const cookies = document.cookie.split('; ');
    const orgSettingsCookie = cookies.find(row => row.startsWith('orgSettings='));
    if (orgSettingsCookie !== undefined) {
        try {
            const decodedValue = decodeURIComponent(orgSettingsCookie.split('=')[1]);
            const parsed = JSON.parse(decodedValue);
            log.debug('loadSettingsFromCookie', `Loaded org settings from cookie: ${JSON.stringify(parsed)}`);
            return {
                timezone: isDefinedAndNotEmpty(parsed.timezone) != false ? parsed.timezone : 'UTC', 
                language: isDefinedAndNotEmpty(parsed.language) != false ? parsed.language : 'en',
                allowedSources: parsed.allowedSources !== undefined ? normalizeStringArray(parsed.allowedSources) : null
            };
        } catch (e) {
            log.error('loadSettingsFromCookie', 'Error parsing orgSettings cookie:', e);
        }
    }
    return { timezone: 'UTC', language: 'en', allowedSources: null }; 
}


const initialSettings = loadSettingsFromCookie();
const resolvedApiEndpoint = normalizeBaseUrl(getRuntimeEnvValue('VUE_APP_API_URL'));
const initialUserInfo = loadUserInfoFromCookie();
const authUsesHttpOnlyCookies = isHttpOnlyAuthEnabled();

export const globalState = reactive({
  userInfo: initialUserInfo,
  userInfoValidatedAt: null,
  apiEndpoint: resolvedApiEndpoint,
  isCustomCodeApplied: false,
  orgSettings: initialSettings,
  orgTimezone: initialSettings.timezone, 
  orgLanguage: initialSettings.language,  
  authUsesHttpOnlyCookies: authUsesHttpOnlyCookies,
  whitelabel: loadWhiteLabelSettings(),
  whiteLabelName: isDefinedAndNotEmpty(getRuntimeEnvValue('VUE_APP_WHITELABEL_NAME')) != false ? getRuntimeEnvValue('VUE_APP_WHITELABEL_NAME') : (isDefinedAndNotEmpty(getRuntimeEnvValue('VUE_APP_WHITE_LABEL_COMPANY_NAME')) != false ? getRuntimeEnvValue('VUE_APP_WHITE_LABEL_COMPANY_NAME') : 'GenChatAI'),
  triggerOrgStyleRefresh: null
});

export function updateOrgSettings(timezone, language, allowedSourcesValue) {

    log.debug('updateOrgSettings', `Updating org settings: ${timezone}, ${language}`);
    const newTimezone = isDefinedAndNotEmpty(timezone) != false ? timezone : 'UTC';
    const newLanguage = isDefinedAndNotEmpty(language) != false ? language : 'en';
    const allowedSources = normalizeStringArray(allowedSourcesValue !== undefined ? allowedSourcesValue : globalState.orgSettings?.allowedSources);
    
    globalState.orgTimezone = newTimezone; 
    globalState.orgLanguage = newLanguage; 
    globalState.orgSettings = {
        timezone: newTimezone,
        language: newLanguage,
        allowedSources: allowedSources
    };
    
    saveSettingsToCookie({ timezone: newTimezone, language: newLanguage, allowedSources: allowedSources });
}

export function updateWhitelabelLogo(logoUrl) {
    const envLogo = getRuntimeEnvValue('VUE_APP_WHITELABEL_LOGO') !== null ? getRuntimeEnvValue('VUE_APP_WHITELABEL_LOGO') : getRuntimeEnvValue('VUE_APP_WHITE_LABEL_LOGO_URL');
    const fallbackLogo = isDefinedAndNotEmpty(envLogo) != false ? String(envLogo).trim() : null;
    if (globalState.whitelabel === undefined || globalState.whitelabel === null) {
        globalState.whitelabel = {};
    }
    const vendorLogo = isDefinedAndNotEmpty(globalState.whitelabel.vendorLogoUrl) != false ? String(globalState.whitelabel.vendorLogoUrl).trim() : null;
    if (isDefinedAndNotEmpty(logoUrl) != false) {
        globalState.whitelabel.logoUrl = String(logoUrl).trim();
    } else if (fallbackLogo !== null) {
        globalState.whitelabel.logoUrl = fallbackLogo;
    } else if (vendorLogo !== null) {
        globalState.whitelabel.logoUrl = vendorLogo;
    } else {
        globalState.whitelabel.logoUrl = null;
    }
    if (globalState.whitelabel.logoUrl !== null && globalState.whitelabel.logoUrl !== undefined && globalState.whitelabel.logoUrl !== '') {
        globalState.whitelabel.enabled = true;
    }
}

export function updateWhitelabelSettings(enabled, logoUrl, appName) {
    const envLogo = getRuntimeEnvValue('VUE_APP_WHITELABEL_LOGO') !== null ? getRuntimeEnvValue('VUE_APP_WHITELABEL_LOGO') : getRuntimeEnvValue('VUE_APP_WHITE_LABEL_LOGO_URL');
    const envName = getRuntimeEnvValue('VUE_APP_WHITELABEL_NAME') !== null ? getRuntimeEnvValue('VUE_APP_WHITELABEL_NAME') : getRuntimeEnvValue('VUE_APP_WHITE_LABEL_COMPANY_NAME');
    const fallbackLogo = isDefinedAndNotEmpty(envLogo) != false ? String(envLogo).trim() : null;
    const fallbackAppName = isDefinedAndNotEmpty(envName) != false ? String(envName).trim() : 'GenChatAI';
    const fallbackNameEnabled = isDefinedAndNotEmpty(envName) != false;
    const nextAppName = isDefinedAndNotEmpty(appName) != false ? String(appName).trim() : fallbackAppName;
    if (globalState.whitelabel === undefined || globalState.whitelabel === null) {
        globalState.whitelabel = {};
    }
    const organizationEnabled = enabled === true;
    const organizationLogo = organizationEnabled === true && isDefinedAndNotEmpty(logoUrl) != false ? String(logoUrl).trim() : null;
    const vendorLogo = isDefinedAndNotEmpty(globalState.whitelabel.vendorLogoUrl) != false ? String(globalState.whitelabel.vendorLogoUrl).trim() : null;
    const whiteLabelEnabled = organizationEnabled === true || vendorLogo !== null || fallbackLogo !== null || fallbackNameEnabled === true;
    globalState.whitelabel.organizationEnabled = organizationEnabled;
    globalState.whitelabel.organizationLogoUrl = organizationLogo;
    globalState.whitelabel.enabled = whiteLabelEnabled;
    globalState.whitelabel.appName = nextAppName;
    globalState.whiteLabelName = nextAppName;
    if (organizationLogo !== null) {
        globalState.whitelabel.logoUrl = organizationLogo;
        return;
    }
    if (vendorLogo !== null) {
        globalState.whitelabel.logoUrl = vendorLogo;
        return;
    }
    if (fallbackLogo !== null) {
        globalState.whitelabel.logoUrl = fallbackLogo;
        return;
    }
    globalState.whitelabel.logoUrl = null;
}

function getCurrentOriginValue() {
    if (typeof window === 'undefined') {
        return '';
    }
    if (window.location === undefined || window.location === null) {
        return '';
    }
    return typeof window.location.origin === 'string' ? window.location.origin : '';
}

function sanitizeWhitelabelLogoUrl(rawValue) {
    if (typeof rawValue !== 'string') {
        return '';
    }
    const value = rawValue.trim();
    if (value === '') {
        return '';
    }
    if (value.startsWith('data:image/') === true) {
        return value;
    }
    if (value.startsWith('/') === true) {
        return value;
    }
    if (typeof window === 'undefined' || window.location === undefined || window.location === null) {
        return '';
    }
    let parsed = null;
    try {
        parsed = new URL(value, window.location.origin);
    } catch (e) {
        parsed = null;
    }
    if (parsed === null) {
        return '';
    }
    const protocol = typeof parsed.protocol === 'string' ? parsed.protocol.toLowerCase() : '';
    if (protocol === 'https:') {
        return parsed.href;
    }
    if (protocol === 'http:' && getRuntimeEnvValue('environment') !== 'production') {
        return parsed.href;
    }
    return '';
}

export async function applyVendorBrandingFromCurrentOrigin() {
    const apiBase = typeof globalState.apiEndpoint === 'string' ? globalState.apiEndpoint : '';
    const origin = getCurrentOriginValue();
    if (apiBase === '' || origin === '') {
        return false;
    }
    let response = null;
    try {
        response = await fetch(`${apiBase}/api/organizationSettings/public/vendorBranding?origin=${encodeURIComponent(origin)}`, {
            credentials: 'include',
            cache: 'no-store'
        });
    } catch (e) {
        return false;
    }
    if (response === null || response.ok !== true) {
        return false;
    }
    let data = null;
    try {
        data = await response.json();
    } catch (e) {
        data = null;
    }
    const vendorBranding = data !== null && data !== undefined && data.matched === true && data.vendorBranding !== undefined && data.vendorBranding !== null ? data.vendorBranding : null;
    const vendorLogo = vendorBranding !== null && typeof vendorBranding.vendorLogo === 'string' ? sanitizeWhitelabelLogoUrl(vendorBranding.vendorLogo) : '';
    if (vendorLogo === '') {
        return false;
    }
    if (globalState.whitelabel === undefined || globalState.whitelabel === null) {
        globalState.whitelabel = {};
    }
    const organizationEnabled = globalState.whitelabel.organizationEnabled === true;
    const organizationLogo = organizationEnabled === true && isDefinedAndNotEmpty(globalState.whitelabel.organizationLogoUrl) != false ? String(globalState.whitelabel.organizationLogoUrl).trim() : null;
    globalState.whitelabel.enabled = true;
    globalState.whitelabel.vendorLogoUrl = vendorLogo;
    if (organizationLogo === null) {
        globalState.whitelabel.logoUrl = vendorLogo;
    }
    return true;
}

export function clearOrgSettingsCookie() {
    log.debug('clearOrgSettingsCookie', 'Clearing orgSettings cookie.');
    const runtimeEnv = getRuntimeEnvValue('environment');
    const sameSiteLax = (runtimeEnv === 'production') ? '; SameSite=Lax' : '';
    document.cookie = `orgSettings=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${sameSiteLax}`;
}

export function formatDateBasedOnOrgSettings(dateValue) {
    if (dateValue === undefined || dateValue === null) {
        return ''; 
    }

    try {
        const dateObject = new Date(dateValue);
        
        if (isNaN(dateObject.getTime()) === true) {
             log.warn('formatDateBasedOnOrgSettings', `Invalid date value: ${dateValue}`);
            return String(dateValue); 
        }


        const locale = isDefinedAndNotEmpty(globalState.orgLanguage) != false ? globalState.orgLanguage : 'en'; 
        const timeZone = isDefinedAndNotEmpty(globalState.orgTimezone) != false ? globalState.orgTimezone : 'UTC';
     

        let formattedDate;

        const formatOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
        };



        const formatLocale = 'en-US'; 

        formattedDate = new Intl.DateTimeFormat(formatLocale, formatOptions).format(dateObject);
        formattedDate = formattedDate.replace(/\u200E|\u200F/g, '');

        return formattedDate;

    } catch (error) {
        log.error('formatDateBasedOnOrgSettings', 'Error formatting date:', error);
        return String(dateValue); 
    }
}
